import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";

const EditOrgLevelTypeModal = ({
  opened,
  onClose,
  onSave,
  onDelete,
  orgLevelType,
}) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const isTop = !!orgLevelType?.top_org_level_type;
  const isBase = !!orgLevelType?.base_org_level_type;
  const canDelete = !isTop && !isBase;

  useEffect(() => {
    if (opened && orgLevelType) {
      setName(orgLevelType.name || "");
      setError("");
      setShowDeleteConfirmation(false);
    }
  }, [opened, orgLevelType]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = { ...orgLevelType, name: name.trim() };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save sales group type");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setShowDeleteConfirmation(false);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
    setError("");
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await onDelete(orgLevelType.id);
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete sales group type");
      setShowDeleteConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Sales Group Type"
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Name"
          placeholder="Enter sales group type name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />

        {showDeleteConfirmation && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                Are you sure you want to delete "{orgLevelType?.name}"? This
                action cannot be undone.
              </Text>
              <Group gap="sm">
                <Button
                  size="xs"
                  color="red"
                  onClick={handleDeleteConfirm}
                  loading={loading}
                >
                  Delete
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" gap="sm">
          {canDelete && onDelete && !showDeleteConfirmation && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteClick}
              disabled={loading}
            >
              Delete
            </Button>
          )}

          <Group justify="flex-end" gap="sm" style={{ marginLeft: "auto" }}>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim() || showDeleteConfirmation}
            >
              Save Changes
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditOrgLevelTypeModal;
